/**
 * FilePickerDialog Component
 *
 * Dialog for selecting files from the FIAS file system to use as AI context.
 */

import { useState, useCallback } from 'react';
import { Paperclip, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileBrowser } from '@/components/files/FileBrowser';
import { api, UserFileResponse } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import {
  SUPPORTED_EXTENSIONS,
  MAX_FILE_SIZE,
  formatBytes,
} from '@/lib/hooks/useFileContext';

export interface FilePickerDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Callback when file content is loaded and ready to be added */
  onFileSelected: (file: { name: string; content: string; mimeType?: string }) => void;
  /** Whether file is currently being processed */
  isProcessing?: boolean;
}

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? `.${ext}` : '';
}

/**
 * Check if a file is supported based on extension/mime type
 */
function isFileSupported(file: UserFileResponse): boolean {
  const ext = getFileExtension(file.fileName);
  if (SUPPORTED_EXTENSIONS.has(ext)) return true;

  // Check MIME type
  if (file.mimeType?.startsWith('text/')) return true;
  if (file.mimeType === 'application/json') return true;
  if (file.mimeType === 'application/pdf') return true;
  if (file.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true;

  return false;
}

/**
 * Extract text from DOCX file using mammoth (dynamically imported)
 */
async function extractTextFromDocx(arrayBuffer: ArrayBuffer): Promise<string> {
  const mammoth = await import('mammoth');
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value.trim();
}

/**
 * Extract text from PDF file using pdf.js (dynamically imported)
 */
async function extractTextFromPdf(arrayBuffer: ArrayBuffer): Promise<string> {
  const pdfjsLib = await import('pdfjs-dist');

  // Set up the worker
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).toString();

  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const textParts: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .map((item) => ('str' in item ? item.str : ''))
      .join(' ');
    textParts.push(pageText);
  }

  return textParts.join('\n\n').trim();
}

/**
 * FilePickerDialog - Select files from FIAS file system as AI context
 */
export function FilePickerDialog({
  open,
  onOpenChange,
  onFileSelected,
  isProcessing: externalProcessing = false,
}: FilePickerDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<UserFileResponse | null>(null);

  const isProcessing = isLoading || externalProcessing;

  const handleFileSelect = useCallback((file: UserFileResponse) => {
    // Check if file type is supported
    if (!isFileSupported(file)) {
      toast({
        title: 'Unsupported file type',
        description: 'Only text files, .docx, and .pdf are supported.',
        variant: 'destructive',
      });
      return;
    }

    // Check file size
    if (file.sizeBytes > MAX_FILE_SIZE) {
      toast({
        title: 'File too large',
        description: `File exceeds maximum size of ${formatBytes(MAX_FILE_SIZE)}`,
        variant: 'destructive',
      });
      return;
    }

    setSelectedFile(file);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    try {
      // Get presigned download URL
      const { downloadUrl } = await api.getDownloadUrl(selectedFile.fileId);

      // Fetch file content
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const ext = getFileExtension(selectedFile.fileName);
      let content: string;

      if (ext === '.pdf' || selectedFile.mimeType === 'application/pdf') {
        // Extract text from PDF
        const arrayBuffer = await response.arrayBuffer();
        content = await extractTextFromPdf(arrayBuffer);
      } else if (
        ext === '.docx' ||
        selectedFile.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        // Extract text from DOCX
        const arrayBuffer = await response.arrayBuffer();
        content = await extractTextFromDocx(arrayBuffer);
      } else {
        // Read as text
        content = await response.text();
      }

      if (!content || content.trim().length === 0) {
        toast({
          title: 'No content',
          description: 'No text content could be extracted from this file.',
          variant: 'destructive',
        });
        return;
      }

      // Pass to parent
      onFileSelected({
        name: selectedFile.fileName,
        content: content.trim(),
        mimeType: selectedFile.mimeType || undefined,
      });

      // Close dialog and reset
      setSelectedFile(null);
      onOpenChange(false);

      toast({
        description: `Added "${selectedFile.fileName}" as context`,
      });
    } catch (err) {
      console.error('Failed to load file:', err);
      toast({
        title: 'Failed to load file',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, onFileSelected, onOpenChange]);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!isProcessing) {
      if (!newOpen) {
        setSelectedFile(null);
      }
      onOpenChange(newOpen);
    }
  }, [isProcessing, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5" />
            Select File as Context
          </DialogTitle>
          <DialogDescription>
            Choose a file from your storage to add as context for the AI.
            Supported: text files, .docx, .pdf
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden border rounded-lg">
          <FileBrowser
            onFileSelect={handleFileSelect}
            multiSelect={false}
            status="active"
            className="h-full"
          />
        </div>

        {selectedFile && (
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 min-w-0">
              <Paperclip className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate font-medium">{selectedFile.fileName}</span>
              <span className="text-sm text-muted-foreground">
                ({formatBytes(selectedFile.sizeBytes)})
              </span>
            </div>
            <Button
              onClick={handleConfirm}
              disabled={isProcessing}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Add as Context'
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default FilePickerDialog;
